/**
 * MaturityActionPlanCard — hybrid action-plan surface.
 *
 * Calls /api/maturity-action-plan?pillar_id=<id> on mount (and
 * whenever `pillarId` changes), then renders:
 *
 *   1. AI context paragraph at the top (60–100 words of plain-
 *      English coaching, generated by Claude Sonnet 4.5 against
 *      the org's actual pillar score + overall maturity).
 *   2. Three static action anchors below — title, description,
 *      owner, horizon. These render even if the AI call fails so
 *      the user always gets something actionable.
 *
 * "Refresh insight" forces a cache bust by appending a no-store
 * query param. The backend cache is keyed by score *bucket*, not
 * raw score, so a refresh inside the same band returns the same
 * paragraph — that's intentional. Different band → different copy.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Sparkles, ClipboardCheck, RefreshCw, AlertTriangle, Loader2, Calendar, User, Pin, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TakeActionButton, buildFocusFromEntity } from '../health/HealthActionSpine';
import { toast } from 'sonner';
import api from '../../lib/api';
import logger from '../../lib/logger';

// ───── Helpers ─────────────────────────────────────────────────
// Build the description for a quarterly-deck pin. The Pin schema
// caps `description` at 400 chars, so we trim the AI paragraph to
// fit cleanly with an ellipsis if needed. AI text often runs ~95
// words ≈ 550 chars, so trimming is the common case.
const PIN_DESCRIPTION_LIMIT = 400;
const buildPinDescription = (data) => {
  const ai = data?.ai_context?.text;
  const fallback = `Action plan for ${data?.pillar_name || 'your weakest maturity pillar'} (score ${data?.pillar_score}/100, ${data?.band || 'unbanded'}).`;
  const text = ai && ai.trim() ? ai : fallback;
  if (text.length <= PIN_DESCRIPTION_LIMIT) return text;
  return text.slice(0, PIN_DESCRIPTION_LIMIT - 1).trimEnd() + '…';
};

// Build the 3 hero stats that appear on the pinned tile (max 8
// allowed; we use 3 — enough to convey "where I am, what's at
// stake, what to do next" at a glance on the home Quarterly Deck).
const buildPinStats = (data) => {
  const stats = [];
  if (typeof data?.pillar_score === 'number') {
    stats.push({
      label: 'Pillar score',
      value: `${data.pillar_score}/100`,
      hint: data.band ? `Band: ${data.band}` : undefined,
    });
  }
  if (typeof data?.overall_score === 'number') {
    stats.push({
      label: 'Org maturity',
      value: `${data.overall_score}/100`,
    });
  }
  // First action is the "first 30 days" move — most actionable
  // starting point for board readers who skim.
  const firstAction = data?.actions?.[0];
  if (firstAction?.title) {
    stats.push({
      label: 'First move',
      value: firstAction.title.length > 26
        ? firstAction.title.slice(0, 25) + '…'
        : firstAction.title,
      hint: firstAction.horizon ? `Horizon: ${firstAction.horizon}` : undefined,
    });
  }
  return stats;
};

export default function MaturityActionPlanCard({ pillarId, pillarName }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [pinned, setPinned] = useState(false);

  const load = useCallback(async () => {
    if (!pillarId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/maturity-action-plan?pillar_id=${pillarId}`);
      setData(res.data);
    } catch (e) {
      logger.error('Action plan load failed:', e);
      setError(e?.response?.data?.detail || 'Could not load action plan');
    } finally {
      setLoading(false);
    }
  }, [pillarId]);

  useEffect(() => { load(); }, [load]);

  // Reset the "Pinned ✓" affordance when the active pillar
  // changes, otherwise the button would mislead the user into
  // thinking they'd already pinned a *different* pillar.
  useEffect(() => { setPinned(false); }, [pillarId]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handlePin = async () => {
    if (!data || pinning) return;
    setPinning(true);
    try {
      await api.post('/quarterly-deck/pins', {
        path: '/maturity-health',
        title: `Maturity Action: ${data.pillar_name || 'weakest pillar'}`,
        description: buildPinDescription(data),
        theme: 'maturity-action',
        stats: buildPinStats(data),
        // The note carries the AI paragraph in full (also capped
        // at 400) so it's visible on hover/expand even if the
        // primary description gets truncated for the tile.
        note: data.ai_context?.text
          ? data.ai_context.text.slice(0, 400)
          : null,
      });
      setPinned(true);
      toast.success('Action plan pinned to Quarterly Deck');
    } catch (err) {
      logger.error('Action plan pin failed:', err);
      toast.error('Could not pin — try again');
    } finally {
      setPinning(false);
    }
  };

  if (!pillarId) return null;

  return (
    <Card data-testid="maturity-action-plan-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardCheck className="h-5 w-5 text-rose-600" />
            Action plan: {data?.pillar_name || pillarName || 'this pillar'}
          </CardTitle>
          <div className="flex items-center gap-1">
            <TakeActionButton
              focus={buildFocusFromEntity('maturity', data && { entity_id: pillarId, entity_label: data.pillar_name || pillarName, score: data.pillar_score, overall_score: data.overall_score })}
              navigate={navigate}
              testId="maturity-action-plan-take-action"
            />
            <Button
              variant={pinned ? 'outline' : 'default'}
              size="sm"
              onClick={handlePin}
              disabled={pinning || !data || loading}
              className={pinned
                ? 'text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                : 'text-xs bg-slate-900 hover:bg-slate-800 text-white'
              }
              data-testid="maturity-action-plan-pin"
            >
              {pinning ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              ) : pinned ? (
                <Check className="h-3.5 w-3.5 mr-1" />
              ) : (
                <Pin className="h-3.5 w-3.5 mr-1" />
              )}
              {pinned ? 'Pinned to Deck' : 'Pin to Quarterly Deck'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="text-xs"
              data-testid="maturity-action-plan-refresh"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
        {data && (
          <p className="text-xs text-slate-500 mt-1">
            Score <strong>{data.pillar_score}/100</strong>
            {data.overall_score != null && (
              <> · org maturity {data.overall_score} ({data.band})</>
            )}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-4" data-testid="maturity-action-plan-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading personalized action plan…
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3" data-testid="maturity-action-plan-error">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        )}

        {data && (
          <>
            {/* AI coaching paragraph — leads the card so users
                read the "why this matters" before the static
                anchors. Shows a small badge so they know it's
                AI-generated. */}
            {data.ai_context?.text ? (
              <div
                className="bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200 rounded-md p-4"
                data-testid="maturity-action-plan-ai-context"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                  <span className="text-[11px] uppercase tracking-wider font-bold text-violet-700">
                    Personalized coaching
                  </span>
                  {data.ai_context.cached && (
                    <Badge variant="outline" className="text-[9px] py-0 px-1.5 text-violet-600 border-violet-300">
                      cached
                    </Badge>
                  )}
                </div>
                <p className="text-sm text-slate-800 leading-relaxed">
                  {data.ai_context.text}
                </p>
              </div>
            ) : data.ai_context_status === 'no_assessment' ? (
              <div className="text-xs text-slate-500 italic" data-testid="maturity-action-plan-no-assessment">
                Personalized coaching will appear once you've completed a maturity assessment.
              </div>
            ) : (
              <div className="text-xs text-slate-500 italic" data-testid="maturity-action-plan-ai-degraded">
                Personalized coaching is temporarily unavailable. Static action plan below is still your best next-step.
              </div>
            )}

            {/* Static playbook actions — always rendered, AI or
                no AI. Numbered so the user reads them as an
                ordered "first 30 / 60 / 90 days" sequence. */}
            <div>
              <p className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                Your next 3 moves
              </p>
              <ol className="space-y-2.5" data-testid="maturity-action-plan-actions">
                {(data.actions || []).map((a, i) => (
                  <li
                    key={a.title}
                    className="flex gap-3 p-3 rounded-md border border-slate-200 hover:border-slate-300 transition-colors"
                    data-testid={`maturity-action-${i + 1}`}
                  >
                    <div className="shrink-0 w-7 h-7 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{a.title}</p>
                      <p className="text-[12px] text-slate-600 mt-0.5 leading-snug">
                        {a.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-3 mt-1.5 text-[11px] text-slate-500">
                        {a.owner && (
                          <span className="flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {a.owner}
                          </span>
                        )}
                        {a.horizon && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {a.horizon}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
